import { useRef, useState } from 'react';

import { Box, Flex, useTheme, Divider } from '@chakra-ui/react';
import { Button as MuiButton, Menu, MenuItem } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { deleteGoalFromGlobalGoalsMap } from 'actions/globalGoalsMapActions';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { DELETE_INITIATIVE_PRIVILEGES } from 'components/RabbitInitiativeSidePanel/accessControls/useInitiativeAccessControl';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getDueDate } from 'screens/oneOnOne/landingPage/LandingPageV2/DateHelpers';
import styled from 'styled-components';

import { OpenSidePanel, Delete, ThreeDots } from 'assets/assetsV1/iconComponents';

import { GOAL_VISIBILITY_OPTIONS } from 'constants/ProjectConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import { deleteTask } from 'services/task';
import { showTaskDetails } from 'services/taskDetails';

import { Avatar } from 'uiToolkitV1/Avatar/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup/';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';
import { Typography } from 'uiToolkitV1/Typography/Typography';

export const SimpleComponent = (p) => {
  const value = p.data?.toIds;
  return (
    <Box display="flex" height="100%" alignItems="center">
      <AvatarGroup max={3}>
        {value?.map((owner) => (
          <Avatar name={owner?.displayName} src={owner?.profilePhoto} />
        ))}
      </AvatarGroup>
    </Box>
  );
};

export const ParentPostComponent = (p) => {
  // const value = p.value;
  const parentName = p.data?.parentDetails?.title;
  return (
    <Flex height="100%" alignItems="center">
      {' '}
      <TextBodyMedium>{parentName}</TextBodyMedium>
    </Flex>
  );
};

export const InitiativesNameComponent = ({ value }) => {
  const { title, isCompleted } = value;
  return (
    <Flex height="100%" alignItems="center">
      <TextBodyMedium style={isCompleted ? { textDecoration: 'line-through' } : { textDecoration: 'none' }}>
        {title}
      </TextBodyMedium>
    </Flex>
  );
};

export const DueDate = (p) => {
  const theme = useTheme();

  const value = p.data?.dueTime;
  const isCompleted = p.data?.status === 'COMPLETED';
  const isNoDueDate = p.data?.dueTime?.timeStamp === null;

  if (isNoDueDate) {
    return (
      <Flex height="100%" alignItems="center">
        <Flex className="info">
          <Flex flexDirection="column">
            <>
              <TextBodyMedium
                fontWeight="400"
                fontSize="14px"
                lineHeight="20px"
                color={theme.colors.functional['333333']}
              >
                No due date{' '}
              </TextBodyMedium>
            </>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex height="100%" alignItems="center">
      <Flex className="info">
        <Flex flexDirection="column">
          <>
            <TextBodyMedium
              fontWeight="400"
              fontSize="14px"
              lineHeight="20px"
              color={theme.colors.functional['333333']}
            >
              {getDueDate(value?.timeStamp)}
            </TextBodyMedium>
            <TextLabel
              fontWeight="400"
              fontSize="12px"
              lineHeight="134.2%"
              color={theme.colors.secondary['666666']}
            >{`${format(new Date(value?.timeStamp), 'MMM d, y')}`}</TextLabel>
          </>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const KebabTableMenuInitiatives = (p) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const history = useHistory();
  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const accessPrivilegesList = p.value;

  const canDeleteInitiativePrivilege =
    p.data?.visibilityType === GOAL_VISIBILITY_OPTIONS[1].value
      ? accessPrivilegesList?.includes(DELETE_INITIATIVE_PRIVILEGES.DELETE_PUBLIC_INITIATIVE)
      : accessPrivilegesList?.includes(DELETE_INITIATIVE_PRIVILEGES.DELETE_ALL_INITIATIVE);

  const isDeletionAllowed = canDeleteInitiativePrivilege;

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const refetchData = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeCardsData]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.mySummary.mySummaryInitiatives]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.MyInitiativeTableData]);
  };
  const onDeleteClick = () => {
    deleteTask({ id: p.data?.uuid })
      .then((res) => {
        refetchData();
        dispatch(deleteGoalFromGlobalGoalsMap({ goalId: p.data?.uuid }));
        dispatch(showSuccessSnackbar(`Deleted successfully.`));
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('There is some problem. Please try again!'));
      });
    closeMenu();
  };

  const openDetailsPanel = () => {
    const { type = 'TASK', uuid } = p.data;
    dispatch(
      showTaskDetails({
        taskIds: [uuid],
        onDelete: () => {},
        onUpdate: (task) => {},
        employee: {},
        type,
        history,
      })
    );
    closeMenu();
  };

  return (
    <Flex height="100%" alignItems="center" justifyContent="center">
      <MuiButton
        aria-controls="simple-menu"
        aria-haspopup="true"
        disableRipple="true"
        onClick={openMenu}
        style={{ backgroundColor: anchorEl ? '#e6f1fd' : 'unset', minWidth: 'unset' }}
      >
        <ThreeDots sx={{ transform: 'rotate(90deg)' }} boxSize="24px" />
      </MuiButton>
      <StyledMenu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{ marginTop: '1rem' }}
      >
        <StyledMenuItem
          onClick={() => {
            openDetailsPanel();
          }}
        >
          <OpenSidePanel boxSize="20px" />
          <Typography ml={2}>View Details</Typography>
        </StyledMenuItem>
        {isDeletionAllowed && (
          <>
            <Divider />

            <StyledMenuItem onClick={onDeleteClick}>
              <Delete boxSize="20px" />
              <Typography ml={2}>Delete</Typography>
            </StyledMenuItem>
          </>
        )}
      </StyledMenu>
    </Flex>
  );
};

const StyledMenuItem = styled(MenuItem)`
  padding: 8px 16px 8px 6px;
  border-radius: 4px;

  font-family: inherit;

  &:hover {
    color: #104f95;
    background-color: #e6f1fd;

    svg {
      color: #104f95;
    }
  }
`;

const StyledMenu = styled(Menu)`
  ul {
    padding: 8px;
  }
`;
